import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  Grid,
  Input,
  Menu,
  message,
  Modal,
  Popover,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import styled from "styled-components";
import {
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import * as dayjs from "dayjs";
import { useFirestore, useFirestoreConnect } from "react-redux-firebase";
import { FIRESTORE_PURCHASE_ORDERS_TABLE } from "../../../../constants/FirestoreConstant";
import {
  PURCHASE_ORDER_SELECTABLE_STATUSES,
  PURCHASE_ORDER_STATUS_CANCELLED,
  PURCHASE_ORDER_STATUS_COMPLETED,
  PURCHASE_ORDER_STATUS_DRAFT,
  PURCHASE_ORDER_STATUS_PENDING_APPROVAL,
  PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION,
  PURCHASE_ORDER_STATUS_RECEIVED,
} from "../../../../constants/OrderConstant";
import { useTranslation } from "react-i18next";
import { addLogEntry } from "../../../../utils/logging";
import { useSuppliers } from "../../../../data/hooks/useSuppliers";
import { usePurchaseOrderShipments } from "../../../../data/hooks/usePurchaseOrderShipments";
import urls from "../../../../urls";
import EllipsisDropdown from "../../../../components/shared-components/EllipsisDropdown";
import { ListCard, Num, PageHeader, RecordLink } from "../../../../components/shared-components/purchase";
import {
  formatPriceByCurrency,
  formatPriceUsd,
  getPurchaseOrderStatusColor,
} from "../../../../functions/formatters";
import { sortPurchaseOrdersByNumberDesc } from "../../../../functions/purchaseOrders";
import { describeWhyActive, findOrdersReadyToComplete, summarizeOrder } from "../../../../functions/purchaseOrderCompletion";

const PurchaseOrdersOverview = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [messageApi, contextHolder] = message.useMessage();
  // Deze pagina leest de collectie, dus abonneert de pagina zelf. Stond tot nu
  // toe in layouts/app-layout (op elke CMS-pagina) -- zie docs/cms-performance.md.
  useFirestoreConnect([{ collection: FIRESTORE_PURCHASE_ORDERS_TABLE }]);
  const purchaseOrders = useSelector((state) => state.firestoreReducer.ordered.purchaseOrders);
  const { suppliers } = useSuppliers();
  // Verzendingen zijn nodig om te bepalen wat er van een order nog open staat — de kolom "Open"
  // en de opruimknop hangen daaraan (BMX-14).
  const { shipments } = usePurchaseOrderShipments();

  // State declarations must come before useEffect hooks that use them
  const [searchText, setSearchText] = useState(searchParams.get("q") || "");
  const [displayCount, setDisplayCount] = useState(20);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "active");
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get("supplier") || null);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page")) || 1);

  // Load filters from localStorage on component mount
  React.useEffect(() => {
    try {
      const savedFilters = localStorage.getItem("purchaseOrdersFilters");
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);
        if (filters.searchText) setSearchText(filters.searchText);
        if (filters.statusFilter) setStatusFilter(filters.statusFilter);
        if (filters.supplierFilter) setSupplierFilter(filters.supplierFilter);
        if (filters.currentPage) setCurrentPage(filters.currentPage);
      } else {
        // Default: hide cancelled and completed orders
        setStatusFilter("active");
      }
    } catch (error) {
      console.error("Error initializing filters:", error);
      // Fallback to default values if localStorage fails
      setStatusFilter("active");
      setSearchText("");
      setSupplierFilter(null);
      setCurrentPage(1);
    }
  }, []);

  // Save filters to localStorage whenever they change
  React.useEffect(() => {
    try {
      const filters = {
        searchText,
        statusFilter,
        supplierFilter,
        currentPage,
      };
      localStorage.setItem("purchaseOrdersFilters", JSON.stringify(filters));
    } catch (error) {
      console.warn("Failed to save filters to localStorage:", error);
      // Silently fail - filters will work but won't persist
    }
  }, [searchText, statusFilter, supplierFilter, currentPage]);

  const updateSearchParams = (updates) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== "All") params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params, { replace: true });
  };

  const { t } = useTranslation();

  const firestore = useFirestore();
  const profile = useSelector((state) => state.firebaseReducer.profile);
  const { email } = profile;

  const handleUpdateOrderStatus = async (order, newStatus) => {
    try {
      const oldLog = order.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "status_changed",
        user: email,
        oldValue: order.status,
        newValue: newStatus,
      });

      const updateData = {
        status: newStatus,
        log: newLog,
        lastModifiedBy: { email, updatedAt: dayjs().unix() },
      };

      if (newStatus === PURCHASE_ORDER_STATUS_COMPLETED) {
        updateData.completedAt = dayjs().unix();
      } else if (newStatus === PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION) {
        updateData.orderedAt = dayjs().unix();
        updateData.completedAt = null;
      } else if (newStatus === PURCHASE_ORDER_STATUS_RECEIVED) {
        updateData.receivedAt = dayjs().unix();
      }

      await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(order.id).update(updateData);

      messageApi.success(t("notifications.purchase_order_status_update_successful"));
    } catch (error) {
      messageApi.error(t("notifications.purchase_order_status_update_failed"));
    }
  };

  /**
   * Orders die allang klaar zijn alsnog afronden (BMX-14).
   *
   * Vanaf nu rondt de Cloud Function een order automatisch af zodra de laatste verzending
   * verwerkt wordt. Die trigger loopt alleen op verzendingen die nog wijzigen, dus alles wat
   * daarvóór al klaar was blijft in het actieve overzicht staan. Deze knop werkt precies die
   * achterstand weg, met dezelfde regel: alle regels verzonden én elke verzending verwerkt.
   *
   * Bewust met een bevestiging die eerst laat zien wélke orders het zijn — afronden haalt ze uit
   * het actieve overzicht, en dat wil je niet per ongeluk over een order heen doen.
   */
  const handleCleanupCompletedOrders = () => {
    const ready = findOrdersReadyToComplete(purchaseOrders || [], shipments);

    if (ready.length === 0) {
      messageApi.info("Er staan geen orders open die volledig verzonden en verwerkt zijn.");
      return;
    }

    Modal.confirm({
      title: `${ready.length} ${ready.length === 1 ? "order" : "orders"} afronden?`,
      width: 520,
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            Van {ready.length === 1 ? "deze order" : "deze orders"} is elke regel verzonden en is elke verzending
            verwerkt. Na het afronden {ready.length === 1 ? "verdwijnt hij" : "verdwijnen ze"} uit het actieve
            overzicht.
          </p>
          <ul style={{ maxHeight: 220, overflowY: "auto", paddingLeft: 20, margin: 0 }}>
            {ready.map((order) => (
              <li key={order.id}>
                {order.orderNumber}
                {order.supplierName ? ` · ${order.supplierName}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ),
      okText: "Afronden",
      cancelText: t("cancel"),
      onOk: async () => {
        const completedAt = dayjs().unix();
        let failed = 0;

        // Bewust één voor één en niet in een batch: bij een fout op één order moeten de andere
        // gewoon doorgaan, en de geschiedenisregel hoort bij de order zelf.
        for (const order of ready) {
          try {
            await firestore
              .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
              .doc(order.id)
              .update({
                status: PURCHASE_ORDER_STATUS_COMPLETED,
                completedAt,
                log: addLogEntry(order.log || [], {
                  action: "status_changed",
                  user: email,
                  oldValue: order.status,
                  newValue: PURCHASE_ORDER_STATUS_COMPLETED,
                }),
                lastModifiedBy: { email, updatedAt: completedAt },
              });

            await firestore
              .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
              .doc(order.id)
              .collection("history")
              .add({
                changedBy: email,
                changedAt: completedAt,
                changes: [
                  {
                    field: "status",
                    oldValue: order.status,
                    newValue: PURCHASE_ORDER_STATUS_COMPLETED,
                    metadata: "Afgerond via opruimen: alles verzonden en verwerkt",
                  },
                ],
              });
          } catch (error) {
            failed += 1;
          }
        }

        const done = ready.length - failed;
        if (done > 0) messageApi.success(`${done} ${done === 1 ? "order" : "orders"} afgerond.`);
        if (failed > 0) messageApi.error(`${failed} ${failed === 1 ? "order" : "orders"} kon niet worden afgerond.`);
      },
    });
  };

  const handleCancelOrder = async (order) => {
    const isDraft = order.status === PURCHASE_ORDER_STATUS_DRAFT;

    Modal.confirm({
      title: isDraft ? t("order.delete_purchase_order") : t("order.cancel_purchase_order"),
      content: isDraft ? t("order.delete_purchase_order_confirm") : t("order.cancel_purchase_order_confirm"),
      okText: t("yes"),
      cancelText: t("no"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          if (isDraft) {
            // Delete draft orders completely
            await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(order.id).delete();
            messageApi.success(t("notifications.purchase_order_deleted_successful"));
          } else {
            // Cancel non-draft orders
            const oldLog = order.log || [];
            const newLog = addLogEntry(oldLog, {
              action: "status_changed",
              user: email,
              oldValue: order.status,
              newValue: PURCHASE_ORDER_STATUS_CANCELLED,
            });

            await firestore
              .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
              .doc(order.id)
              .update({
                status: PURCHASE_ORDER_STATUS_CANCELLED,
                cancelledAt: dayjs().unix(),
                log: newLog,
                lastModifiedBy: { email, updatedAt: dayjs().unix() },
              });

            messageApi.success(t("notifications.purchase_order_cancelled_successful"));
          }
        } catch (error) {
          messageApi.error(
            isDraft
              ? t("notifications.purchase_order_deleted_failed")
              : t("notifications.purchase_order_cancelled_failed"),
          );
        }
      },
    });
  };

  // De aanmaak- en bewerkflow zijn een eigen pagina geworden (views/.../create).
  // Wat hier stond — opslaan, concept laden, productconcepten aanmaken en
  // prijzen terugschrijven — is daarheen verhuisd; deze pagina navigeert er
  // alleen nog naartoe.
  const openAddPage = () => navigate(`${urls.purchaseOrdersList}/new`);
  const openEditPage = (order) => navigate(`${urls.purchaseOrdersList}/${order.id}/edit`);

  const onSearch = (e) => {
    setSearchText(e.target.value);
    setDisplayCount(20);
    setCurrentPage(1);
    updateSearchParams({ q: e.target.value || null, page: null });
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setDisplayCount(20);
    setCurrentPage(1);
    updateSearchParams({ status: value, page: null });
  };

  // Mobile card component
  const PurchaseOrderCard = ({ order }) => {
    return (
      <MobilePurchaseOrderCard onClick={() => navigate(`${urls.purchaseOrdersList}/${order.id}`)}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: 0, marginBottom: 4, fontSize: 16, fontWeight: 600 }}>{order.orderNumber}</h4>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>{order.supplierName}</div>
            <div style={{ fontSize: 12, color: "#888" }}>{dayjs.unix(order.orderDate).format("DD-MM-YYYY")}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <Tag color={getPurchaseOrderStatusColor(order.status)}>{t(`order.${order.status.toLowerCase()}`)}</Tag>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#1890ff" }}>
              {(() => {
                const additionalCostsTotal =
                  order.invoice?.additionalCosts?.reduce((sum, cost) => sum + cost.amount, 0) || 0;
                const productsTotal = order.totalPrice ?? 0;
                const total = productsTotal + additionalCostsTotal;
                return order.currency === "IDR" ? `IDR ${total.toLocaleString("id-ID")}` : formatPriceUsd(total);
              })()}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#666" }}>
          <span>{order.products?.length || 0} producten</span>
          {order.cubicMeters && <span>{order.cubicMeters} m³</span>}
          <Tag>{order.currency || "USD"}</Tag>
        </div>
      </MobilePurchaseOrderCard>
    );
  };
  // Eén lijst voor mobiel én desktop. Die twee stonden apart en waren uit elkaar gelopen:
  // pending_supplier_review, ready_for_production en partially_shipped ontbraken in allebei,
  // waardoor je juist niet kon filteren op de orders die deze story zichtbaar wil houden.
  // Wat er niet meer in staat, staat ook niet meer in PURCHASE_ORDER_SELECTABLE_STATUSES.
  const statusFilterOptions = [
    { value: "active", label: "Actief (zonder geannuleerd/afgerond)" },
    { value: "All", label: t("all") },
    ...PURCHASE_ORDER_SELECTABLE_STATUSES.map((status) => ({
      value: status,
      label: t(`order.${status.toLowerCase()}`),
    })),
  ];

  // Filter and search logic
  const getFilteredOrders = () => {
    if (!purchaseOrders) return [];

    let filtered = [...purchaseOrders];

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          order.orderNumber?.toLowerCase().includes(searchLower) ||
          order.supplierName?.toLowerCase().includes(searchLower),
      );
    }

    // Apply supplier filter
    if (supplierFilter) {
      filtered = filtered.filter((order) => order.supplierId === supplierFilter);
    }

    // Apply status filter
    if (statusFilter && statusFilter !== "All") {
      if (statusFilter === "active") {
        // Hide cancelled and completed orders
        filtered = filtered.filter(
          (order) =>
            order.status !== PURCHASE_ORDER_STATUS_CANCELLED && order.status !== PURCHASE_ORDER_STATUS_COMPLETED,
        );
      } else {
        filtered = filtered.filter((order) => order.status === statusFilter);
      }
    }

    return filtered;
  };

  const tableColumns = [
    {
      title: t("order.order_id"),
      dataIndex: "orderNumber",
      // Geen sorter: de volgorde ligt vast op nieuwste eerst (sortPurchaseOrdersByNumberDesc).
      render: (text, record) => {
        // Een <a onClick> zonder href is voor de browser geen link: hij matcht
        // `a:any-link` niet en Bootstrap zet hem expliciet op `color: inherit`.
        // Vandaar RecordLink (een echte react-router <Link>), net als op
        // Verzendingen en Facturen. Een geannuleerde order blijft grijze,
        // niet-klikbare tekst.
        const orderLink =
          record.status === PURCHASE_ORDER_STATUS_CANCELLED ? (
            <Num muted>{text}</Num>
          ) : (
            <RecordLink to={`${urls.purchaseOrdersList}/${record.id}`}>{text}</RecordLink>
          );

        return orderLink;
      },
    },
    {
      title: t("supplier.supplier"),
      dataIndex: "supplierName",
      render: (name) => name || "-",
    },
    {
      title: t("total_products"),
      dataIndex: "products",
      align: "right",
      render: (products) => <Num>{products ? products.length : 0}</Num>,
    },
    {
      title: t("total_price"),
      dataIndex: "totalPrice",
      align: "right",
      render: (price, record) => {
        const additionalCostsTotal = record.invoice?.additionalCosts?.reduce((sum, cost) => sum + cost.amount, 0) || 0;
        const productsTotal = price || 0;
        const total = productsTotal + additionalCostsTotal;

        if (!total) return <Num muted>-</Num>;
        // formatPriceByCurrency dekt IDR al; de eigen tak hier deed hetzelfde.
        return <Num>{formatPriceByCurrency(total, record.currency)}</Num>;
      },
    },
    {
      title: t("currency"),
      dataIndex: "currency",
      render: (currency) => <Tag>{currency || "USD"}</Tag>,
    },
    {
      title: t("cubic_meters"),
      dataIndex: "cubicMeters",
      align: "right",
      render: (meters) => (meters ? <Num>{meters} m³</Num> : <Num muted>—</Num>),
    },
    {
      // Waarom staat deze order nog in het actieve overzicht (BMX-14)? Zonder deze kolom is dat
      // alleen te achterhalen door elke order open te klikken en de regels na te lopen.
      title: "Open",
      key: "open",
      align: "right",
      render: (_, record) => {
        if (record.status === PURCHASE_ORDER_STATUS_CANCELLED || record.status === PURCHASE_ORDER_STATUS_COMPLETED) {
          return <Num muted>—</Num>;
        }

        const summary = summarizeOrder(record, shipments);
        const reason = describeWhyActive(record, shipments);

        return (
          <Tooltip title={reason}>
            {summary.openQuantity > 0 ? (
              <Num>{summary.openQuantity.toLocaleString("nl-NL")}</Num>
            ) : (
              <Num muted>{summary.fullyProcessed ? "klaar" : "0"}</Num>
            )}
          </Tooltip>
        );
      },
    },
    {
      title: t("order.status"),
      dataIndex: "status",
      render: (status, record) => {
        const statusTag = <Tag color={getPurchaseOrderStatusColor(status)}>{t(`order.${status.toLowerCase()}`)}</Tag>;

        // Add badge for pending_approval
        if (status === PURCHASE_ORDER_STATUS_PENDING_APPROVAL) {
          return <Badge dot>{statusTag}</Badge>;
        }

        // De popover bevat alleen de knop "naar leverancier sturen", en die bestaat alleen voor
        // een concept. Hier stond een lijst met statussen die 'm moesten overslaan, maar die was
        // niet compleet: bij o.a. verzonden en gedeeltelijk verzonden klapte er een lege popover
        // open. Omgedraaid naar de voorwaarde die er echt toe doet.
        if (status !== PURCHASE_ORDER_STATUS_DRAFT) {
          return statusTag;
        }

        return (
          <Popover
            title={
              <div style={{ textAlign: "center" }}>
                {status === PURCHASE_ORDER_STATUS_DRAFT &&
                  (() => {
                    // Check if order is complete enough to send to supplier
                    const hasProducts = record.products && record.products.length > 0;
                    const allProductsComplete =
                      hasProducts &&
                      record.products.every((p) => {
                        // Draft products only need amount > 0 (supplier fills in the rest)
                        if (p.productDraftId) {
                          return p.amount > 0;
                        }
                        // Regular products need all fields
                        return (
                          p.amount > 0 && p.innerPackagingQuantity > 0 && p.masterPackagingQuantity > 0 && p.price > 0
                        );
                      });
                    const canSendToSupplier = hasProducts && allProductsComplete;

                    let disabledReason = "";
                    if (!hasProducts) disabledReason = t("draft_needs_products");
                    else if (!allProductsComplete) disabledReason = t("draft_products_incomplete");

                    return (
                      <Button
                        type="primary"
                        size="small"
                        disabled={!canSendToSupplier}
                        onClick={() => handleUpdateOrderStatus(record, PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION)}
                        title={disabledReason}
                      >
                        {t("set_to_ordered")}
                      </Button>
                    );
                  })()}
              </div>
            }
          >
            {statusTag}
          </Popover>
        );
      },
    },
    {
      title: t("order.order_date"),
      dataIndex: "orderDate",
      align: "right",
      render: (date) => (date ? <Num>{dayjs.unix(date).format("DD-MM-YYYY")}</Num> : <Num muted>—</Num>),
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, record) => {
        // Don't show actions for cancelled or completed orders
        if (record.status === PURCHASE_ORDER_STATUS_CANCELLED || record.status === PURCHASE_ORDER_STATUS_COMPLETED) {
          return null;
        }

        const menu = (
          <Menu>
            {record.status === PURCHASE_ORDER_STATUS_DRAFT && (
              <Menu.Item onClick={() => openEditPage(record)}>{t("edit")}</Menu.Item>
            )}
            <Menu.Item onClick={() => handleCancelOrder(record)}>
              <span style={{ color: "#ff4d4f" }}>{t("order.cancel_order")}</span>
            </Menu.Item>
          </Menu>
        );

        return <EllipsisDropdown menu={menu} />;
      },
    },
  ];

  const filteredOrders = getFilteredOrders();
  const sortedOrders = sortPurchaseOrdersByNumberDesc(filteredOrders);

  // Regel onder de titel: hoeveel orders er zijn, hoeveel er lopen en hoeveel er
  // op Bombyxx wachten. Dat laatste is de enige groep die actie vraagt, dus die
  // krijgt kleur - anders moet je de statuskolom afspeuren om het te zien.
  // Over álle orders, niet over de gefilterde lijst: met het standaardfilter "actief" filtert
  // die lijst geannuleerd/afgerond er al uit, waardoor dit getal altijd gelijk was aan het
  // totaal erboven en dus niets toevoegde.
  const activeOrderCount = (purchaseOrders || []).filter(
    (order) =>
      order.status !== PURCHASE_ORDER_STATUS_CANCELLED && order.status !== PURCHASE_ORDER_STATUS_COMPLETED,
  ).length;
  // Hoeveel van die actieve orders eigenlijk al klaar zijn — de aanleiding voor de opruimknop.
  const readyToCompleteCount = findOrdersReadyToComplete(purchaseOrders || [], shipments).length;
  const awaitingApprovalCount = filteredOrders.filter(
    (order) => order.status === PURCHASE_ORDER_STATUS_PENDING_APPROVAL,
  ).length;

  const orderCounts = (
    <>
      {filteredOrders.length} orders · {activeOrderCount} actief
      {awaitingApprovalCount > 0 && (
        <>
          {" · "}
          <span style={{ color: "#96601A" }}>
            {awaitingApprovalCount} wacht{awaitingApprovalCount === 1 ? "" : "en"} op goedkeuring
          </span>
        </>
      )}
      {readyToCompleteCount > 0 && (
        <>
          {" · "}
          <span>
            {readyToCompleteCount} klaar om af te ronden
          </span>
        </>
      )}
    </>
  );

  return (
    <>
      {contextHolder}
      <PageHeader
        title={t("order.purchase_orders")}
        subtitle={orderCounts}
        extra={
          !isMobile && (
            <Space>
              {/* Alleen tonen als er ook echt iets op te ruimen valt — een knop die altijd
                  "geen orders gevonden" antwoordt, is een knop die je leert negeren. */}
              {readyToCompleteCount > 0 && (
                <Button onClick={handleCleanupCompletedOrders}>
                  {readyToCompleteCount} {readyToCompleteCount === 1 ? "order" : "orders"} afronden
                </Button>
              )}
              <Button type="primary" icon={<PlusOutlined />} onClick={openAddPage}>
                {t("order.add_purchase_order")}
              </Button>
            </Space>
          )
        }
      />
      {isMobile ? (
        <Card>
          <>
            <div style={{ marginBottom: 16 }}>
              <Input
                placeholder={t("search")}
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={onSearch}
                style={{ marginBottom: 8 }}
              />
              <Select
                allowClear
                placeholder={t("supplier.supplier")}
                value={supplierFilter}
                onChange={(v) => {
                  setSupplierFilter(v);
                  setDisplayCount(20);
                  setCurrentPage(1);
                  updateSearchParams({ supplier: v, page: null });
                }}
                style={{ width: "100%", marginBottom: 8 }}
                options={(suppliers || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((s) => ({ value: s.id, label: s.name || s.id }))}
              />
              <Select
                value={statusFilter}
                onChange={handleStatusFilter}
                style={{ width: "100%", marginBottom: 8 }}
                options={statusFilterOptions}
              />
              <Button type="primary" onClick={openAddPage} style={{ width: "100%" }}>
                {t("order.add_purchase_order")}
              </Button>
            </div>
            <div style={{ padding: "8px 0" }}>
              {sortedOrders && sortedOrders.length > 0 ? (
                <>
                  <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    {sortedOrders.slice(0, displayCount).map((order) => (
                      <PurchaseOrderCard key={order.id} order={order} />
                    ))}
                  </Space>
                  {displayCount < sortedOrders.length && (
                    <div style={{ textAlign: "center", padding: "20px 0" }}>
                      <Button
                        type="default"
                        onClick={() => setDisplayCount((prev) => Math.min(prev + 20, sortedOrders.length))}
                        style={{ minWidth: 200 }}
                      >
                        Laad meer ({sortedOrders.length - displayCount} resterend)
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <Card>
                  <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>{t("no_data")}</div>
                </Card>
              )}
            </div>
          </>
        </Card>
      ) : (
        <ListCard
          filters={
              <Space>
                <Input placeholder={t("search")} prefix={<SearchOutlined />} value={searchText} onChange={onSearch} />
                <Select
                  allowClear
                  placeholder={t("supplier.supplier")}
                  value={supplierFilter}
                  onChange={(v) => {
                    setSupplierFilter(v);
                    setCurrentPage(1);
                    updateSearchParams({ supplier: v, page: null });
                  }}
                  style={{ minWidth: 200 }}
                  options={(suppliers || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((s) => ({ value: s.id, label: s.name || s.id }))}
                />
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilter}
                  style={{ minWidth: 200 }}
                  options={statusFilterOptions}
                />
              </Space>
          }
        >
          <Table
            columns={tableColumns}
            dataSource={sortedOrders}
            rowKey="id"
            scroll={{ x: true }}
            showSorterTooltip={false}
            pagination={{
              current: currentPage,
              showTotal: (total, range) => `${range[0]}-${range[1]} van ${total}`,
              onChange: (page) => {
                setCurrentPage(page);
                updateSearchParams({ page: String(page) });
              },
            }}
          />
        </ListCard>
      )}
    </>
  );
};

const MobilePurchaseOrderCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  margin-bottom: 4px;

  .ant-card-body {
    padding: 12px;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default PurchaseOrdersOverview;
